import { gql } from '../../core/graphql-client.js';
import { auth } from '../../core/auth-store.js';
import { router } from '../../core/router.js';
import { toast } from '../../core/event-bus.js';
import { isDev } from '../../config.js';
import { loadGoogleSignIn, loadFacebookSDK } from '../../core/oauth-loaders.js';
import type { AuthResult, MagicLinkResult } from '../../types.js';

interface AuthConfig {
  googleClientId: string;
  facebookAppId: string;
}

export class LoginPage extends HTMLElement {
  connectedCallback(): void {
    this.innerHTML = `
      <div class="login-container">
        <h1 class="login-title">Logowanie</h1>

        <form id="password-form" class="login-form">
          <div class="form-group">
            <label for="login-contact">Email lub telefon</label>
            <input id="login-contact" type="text" placeholder="email lub numer telefonu" required>
          </div>
          <div class="form-group">
            <label for="login-password">Hasło</label>
            <input id="login-password" type="password" placeholder="••••••••">
          </div>
          <button type="submit" class="btn btn-primary login-submit">Zaloguj się</button>
        </form>

        <div id="login-error" class="login-error" style="display:none;"></div>

        <div id="oauth-section" class="login-oauth" style="display:none;">
          <div class="login-divider"><span>lub</span></div>
          <div id="google-btn-container"></div>
          <button id="fb-login-btn" class="btn btn-facebook" style="display:none;">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style="margin-right:8px;"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
            Zaloguj przez Facebook
          </button>
        </div>

        <div class="login-divider"><span>lub</span></div>

        <button id="magic-link-toggle" class="btn btn-secondary login-magic">Wy\u015blij link do logowania</button>

        <div id="magic-link-section" style="display:none;">
          <p class="login-subtitle">Wy\u015blemy jednorazowy link na podany wy\u017cej adres</p>
          <button class="btn btn-primary login-submit" id="magic-submit" style="width:100%;max-width:360px;">Wyślij link</button>
        </div>

        <div id="login-sent" class="login-sent" style="display:none;">
          <div class="login-sent-icon">✉️</div>
          <p class="login-sent-title">Sprawdź swoją skrzynkę email!</p>
          <p class="login-sent-text">Wysłaliśmy link do logowania na <strong id="sent-email"></strong>.</p>
          <p class="login-sent-hint">Kliknij link w wiadomości, aby się zalogować. Link jest ważny 15 minut.</p>
          <button class="btn btn-secondary login-retry" id="retry-btn">Wyślij ponownie</button>
        </div>

        ${isDev() ? `
        <details class="login-dev">
          <summary>DEV: ręczna weryfikacja</summary>
          <div class="login-dev-content">
            <input id="dev-token" type="text" placeholder="Wklej token z konsoli backendu...">
            <button id="dev-verify" class="btn btn-primary btn-sm">Weryfikuj</button>
          </div>
        </details>
        ` : ''}
      </div>
      <style>
        .login-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: calc(100vh - var(--header-height) - 64px);
          padding: var(--space-lg);
          gap: var(--space-md);
        }
        .login-title { font-size: 1.5rem; margin: 0; }
        .login-subtitle { color: var(--color-on-surface-muted); text-align: center; margin: 0; font-size: .9rem; }
        .login-form { width: 100%; max-width: 360px; display: flex; flex-direction: column; gap: 12px; }
        .login-submit { width: 100%; }
        .login-divider {
          width: 100%;
          max-width: 360px;
          display: flex;
          align-items: center;
          gap: 12px;
          color: var(--color-on-surface-muted);
          font-size: .8rem;
        }
        .login-divider::before, .login-divider::after {
          content: '';
          flex: 1;
          border-top: 1px solid var(--color-border, #ddd);
        }
        .login-oauth {
          width: 100%;
          max-width: 360px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }
        .btn-facebook {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #1877f2;
          color: #fff;
          border: none;
          padding: 10px 16px;
          border-radius: var(--radius-md, 8px);
          font-size: .9rem;
          font-weight: 600;
          cursor: pointer;
        }
        .btn-facebook:hover { background: #166fe5; }
        .login-magic { width: 100%; max-width: 360px; }
        .login-sent {
          width: 100%;
          max-width: 360px;
          text-align: center;
          padding: var(--space-lg) var(--space-md);
          background: var(--color-success-bg, #d4edda);
          border-radius: var(--radius-md, 8px);
        }
        .login-sent-icon { font-size: 2.5rem; margin-bottom: var(--space-sm); }
        .login-sent-title { font-size: 1.1rem; font-weight: 600; margin: 0 0 var(--space-xs); }
        .login-sent-text { font-size: .875rem; margin: 0 0 var(--space-xs); }
        .login-sent-hint { font-size: .8rem; color: var(--color-on-surface-muted); margin: 0 0 var(--space-md); }
        .login-retry { font-size: .8rem; }
        .login-error { color: var(--color-error); font-size: .875rem; text-align: center; max-width: 360px; }
        .login-dev {
          width: 100%;
          max-width: 360px;
          margin-top: var(--space-md);
          font-size: .75rem;
          color: var(--color-on-surface-muted);
        }
        .login-dev summary { cursor: pointer; padding: var(--space-xs) 0; }
        .login-dev-content { display: flex; gap: 8px; margin-top: var(--space-xs); }
        .login-dev-content input { flex: 1; font-size: .8rem; }
        #google-btn-container { width: 100%; display: flex; justify-content: center; }
      </style>
    `;

    const errorDiv = this.querySelector('#login-error') as HTMLElement;

    const showError = (msg: string) => {
      errorDiv.textContent = msg;
      errorDiv.style.display = 'block';
    };

    const hideError = () => { errorDiv.style.display = 'none'; };

    const onLoginSuccess = (result: AuthResult) => {
      auth.login(result);
      router.navigate(auth.isAdmin ? '/admin/dashboard' : '/availability');
    };

    // ── Password form ───────────────────────────────
    const passwordForm = this.querySelector('#password-form') as HTMLFormElement;
    const contactInput = this.querySelector('#login-contact') as HTMLInputElement;
    const passwordInput = this.querySelector('#login-password') as HTMLInputElement;

    passwordForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const contact = contactInput.value.trim();
      const password = passwordInput.value;
      if (!contact) return;
      if (!password) {
        showError('Wpisz has\u0142o.');
        passwordInput.focus();
        return;
      }

      hideError();
      const btn = passwordForm.querySelector('button') as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Logowanie...';

      try {
        const data = await gql.query<{ loginWithPassword: AuthResult }>(
          `mutation($contact: String!, $password: String!) {
            loginWithPassword(contact: $contact, password: $password) { sessionToken personId displayName isAdmin }
          }`,
          { contact, password },
        );
        onLoginSuccess(data.loginWithPassword);
      } catch (e) {
        showError(e instanceof Error ? e.message : String(e));
      }
      btn.disabled = false;
      btn.textContent = 'Zaloguj si\u0119';
    });

    // ── Magic link ──────────────────────────────────
    const magicToggle = this.querySelector('#magic-link-toggle') as HTMLElement;
    const magicSection = this.querySelector('#magic-link-section') as HTMLElement;
    const magicSubmit = this.querySelector('#magic-submit') as HTMLButtonElement;
    const sentDiv = this.querySelector('#login-sent') as HTMLElement;
    const sentEmailSpan = this.querySelector('#sent-email')!;

    magicToggle.addEventListener('click', () => {
      magicToggle.style.display = 'none';
      magicSection.style.display = 'flex';
      magicSection.style.flexDirection = 'column';
      magicSection.style.alignItems = 'center';
      if (!contactInput.value.trim()) contactInput.focus();
    });

    const sendMagicLink = async () => {
      const contact = contactInput.value.trim();
      if (!contact) {
        showError('Wpisz email lub telefon powy\u017cej');
        contactInput.focus();
        return;
      }

      hideError();
      magicSubmit.disabled = true;
      magicSubmit.textContent = 'Wysyłanie...';

      try {
        const data = await gql.query<{ requestMagicLink: MagicLinkResult }>(
          `mutation($contact: String!, $channel: Channel!) {
            requestMagicLink(contact: $contact, channel: $channel) { success message }
          }`,
          { contact, channel: 'EMAIL' },
        );

        if (data.requestMagicLink.success) {
          magicSection.style.display = 'none';
          passwordForm.style.display = 'none';
          sentDiv.style.display = 'block';
          sentEmailSpan.textContent = contact;
        } else {
          showError(data.requestMagicLink.message);
        }
      } catch (e) {
        showError(e instanceof Error ? e.message : String(e));
      }
      magicSubmit.disabled = false;
      magicSubmit.textContent = 'Wyślij link';
    };

    magicSubmit.addEventListener('click', sendMagicLink);

    this.querySelector('#retry-btn')!.addEventListener('click', () => {
      sentDiv.style.display = 'none';
      magicSection.style.display = 'flex';
      passwordForm.style.display = 'flex';
      sendMagicLink();
    });

    // ── Dev token verify ────────────────────────────
    if (isDev()) {
      this.querySelector('#dev-verify')!.addEventListener('click', async () => {
        const token = (this.querySelector('#dev-token') as HTMLInputElement).value.trim();
        if (!token) return;
        try {
          const data = await gql.query<{ verifyMagicLink: AuthResult }>(
            `mutation($token: String!) {
              verifyMagicLink(token: $token) { sessionToken personId displayName isAdmin }
            }`,
            { token },
          );
          onLoginSuccess(data.verifyMagicLink);
        } catch (e) {
          toast(`Błąd weryfikacji: ${e instanceof Error ? e.message : e}`, 'error');
        }
      });
    }

    // ── OAuth (async init) ──────────────────────────
    this.initOAuth(onLoginSuccess, showError);
  }

  private async initOAuth(
    onSuccess: (result: AuthResult) => void,
    showError: (msg: string) => void,
  ): Promise<void> {
    try {
      const { authConfig } = await gql.query<{ authConfig: AuthConfig }>(
        'query { authConfig { googleClientId facebookAppId } }'
      );

      const hasAnyOAuth = !!authConfig.googleClientId || !!authConfig.facebookAppId;
      if (!hasAnyOAuth) return;

      const oauthSection = this.querySelector('#oauth-section') as HTMLElement;
      oauthSection.style.display = 'flex';

      // Google
      if (authConfig.googleClientId) {
        try {
          await loadGoogleSignIn();
          const container = this.querySelector('#google-btn-container') as HTMLElement;
          google.accounts.id.initialize({
            client_id: authConfig.googleClientId,
            callback: async (response) => {
              try {
                const data = await gql.query<{ loginWithGoogle: AuthResult }>(
                  `mutation($idToken: String!) {
                    loginWithGoogle(idToken: $idToken) { sessionToken personId displayName isAdmin }
                  }`,
                  { idToken: response.credential },
                );
                onSuccess(data.loginWithGoogle);
              } catch (e) {
                showError(e instanceof Error ? e.message : String(e));
              }
            },
          });
          google.accounts.id.renderButton(container, {
            theme: 'outline',
            size: 'large',
            width: 360,
            text: 'signin_with',
            locale: 'pl',
          });
        } catch (e) {
          console.warn('Google Sign-In failed to load:', e);
        }
      }

      // Facebook
      if (authConfig.facebookAppId) {
        try {
          await loadFacebookSDK(authConfig.facebookAppId);
          const fbBtn = this.querySelector('#fb-login-btn') as HTMLElement;
          fbBtn.style.display = 'flex';
          fbBtn.addEventListener('click', () => {
            FB.login(async (response) => {
              if (!response.authResponse?.accessToken) return;
              try {
                const data = await gql.query<{ loginWithFacebook: AuthResult }>(
                  `mutation($accessToken: String!) {
                    loginWithFacebook(accessToken: $accessToken) { sessionToken personId displayName isAdmin }
                  }`,
                  { accessToken: response.authResponse.accessToken },
                );
                onSuccess(data.loginWithFacebook);
              } catch (e) {
                showError(e instanceof Error ? e.message : String(e));
              }
            }, { scope: 'email' });
          });
        } catch (e) {
          console.warn('Facebook SDK failed to load:', e);
        }
      }
    } catch {
      // authConfig query failed — OAuth section stays hidden
    }
  }
}

customElements.define('login-page', LoginPage);
